import { useState } from "react";
import FXBackdrop from "../components/FXBackdrop";
import PageTransition from "../components/PageTransition";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Mail as MailIcon, CheckCircle2, ShieldCheck, Clock } from "lucide-react";

const inputCls = "h-11 rounded-xl border px-3 bg-white/80 text-slate-900 outline-none focus:ring-2 focus:ring-violet-300 transition-all duration-300 focus:translate-y-[-1px] focus:shadow-md";
const inputStyle = { borderColor: "rgba(226,232,240,0.9)" };
const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const fd = new FormData(e.target);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fd.get("name"),
          email: fd.get("email"),
          company: fd.get("company"),
          message: fd.get("message"),
        }),
      });
      if (!res.ok) throw new Error("Failed to submit");
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageTransition>
      <FXBackdrop>
        <section className="containerX pt-10 md:pt-16 pb-16 relative">
          <div className="float-shape bg-violet-400 h-12 w-12 top-4 right-8 hidden lg:block" />
          <div className="float-shape-slow bg-purple-300 h-8 w-8 bottom-20 left-2 hidden lg:block" style={{ animationDelay: "1s" }} />

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <div className="chip">
                <span className="h-2 w-2 rounded-full" style={{ background: "rgb(139,92,246)" }} /> Contact
              </div>
              <h1 className="h1 mt-5">Let's hire someone <span className="text-black">great.</span></h1>
              <p className="p mt-4 max-w-xl">
                Share your role requirements and we'll respond with a plan + candidate ETA.
              </p>

              {/* Direct contact info */}
              <div className="mt-6 grid gap-3">
                <div className="flex items-center gap-3 text-sm group">
                  <motion.div
                    className="h-9 w-9 rounded-xl bg-violet-100 flex items-center justify-center"
                    whileHover={{ scale: 1.1, rotate: 6 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <MailIcon size={16} className="text-violet-600" />
                  </motion.div>
                  <div>
                    <div className="text-xs text-slate-400">Email us</div>
                    <a href="mailto:contact@hirrd.com" className="font-medium text-slate-700 hover:text-violet-600 animated-underline transition-colors">contact@hirrd.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm group">
                  <motion.div
                    className="h-9 w-9 rounded-xl bg-violet-100 flex items-center justify-center"
                    whileHover={{ scale: 1.1, rotate: -6 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Phone size={16} className="text-violet-600" />
                  </motion.div>
                  <div>
                    <div className="text-xs text-slate-400">Call us</div>
                    <a href="tel:+12072508698" className="font-medium text-slate-700 hover:text-violet-600 animated-underline transition-colors">+1 207 250 8698</a>
                  </div>
                </div>
              </div>

              <motion.div
                className="mt-8 glass p-6"
                whileHover={{ y: -6, scale: 1.02, boxShadow: "0 20px 50px rgba(139,92,246,0.14)" }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
              >
                <div className="font-semibold">What to include</div>
                <ul className="mt-3 text-sm text-slate-500 grid gap-2">
                  {[
                    "Role, seniority, location/remote",
                    "Must-have skills + dealbreakers",
                    "Salary range",
                    "Interview stages + timeline",
                  ].map((item, i) => (
                    <motion.li key={item}
                      className="flex items-center gap-2"
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: false, amount: 0.1 }}
                      transition={{ delay: 0.4 + i * 0.08, duration: 0.4 }}
                    >
                      <CheckCircle2 size={14} className="text-violet-400 flex-shrink-0" />
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

            <motion.div
              className="mt-4 glass p-5 flex items-start gap-4"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                <ShieldCheck size={20} className="text-violet-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">90-day replacement guarantee</div>
                <p className="text-sm text-slate-500 mt-1">
                  If a placement doesn't work out within 90 days for any performance-related reason,
                  we replace them at no additional fee.
                </p>
              </div>
            </motion.div>

            </div>

            {!submitted ? (
              <motion.form onSubmit={handleSubmit} className="glass p-6 grid gap-3"
                initial={{ opacity: 0, x: 30, scale: 0.97 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                whileHover={{ y: -4, boxShadow: "0 24px 60px rgba(139,92,246,0.12)" }}
                transition={{ duration: 0.6, delay: 0.2, type: "spring", stiffness: 280 }}
              >
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
                  >
                    {error}
                  </motion.div>
                )}
                <input name="name" className={inputCls} style={inputStyle} placeholder="Name" required />
                <input name="email" className={inputCls} style={inputStyle} placeholder="Email" type="email" required />
                <input name="company" className={inputCls} style={inputStyle} placeholder="Company" />
                <textarea name="message" className="min-h-32 rounded-xl border px-3 py-2 bg-white/80 text-slate-900 outline-none focus:ring-2 focus:ring-violet-300 transition-all duration-300 focus:translate-y-[-1px] focus:shadow-md"
                  style={inputStyle} placeholder="Role details (skills, timeline, budget)" required />
                <button type="submit" disabled={loading} className="btn btnPrimary justify-center disabled:opacity-50 pulse-glow">
                  {loading ? "Sending..." : "Send"} {!loading && <ArrowRight size={16} />}
                </button>
                <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mt-1">
                  <Clock size={12} className="text-violet-400 flex-shrink-0" />
                  <span>We respond within 24 hours with a hiring plan and your first candidate ETA.</span>
                </div>
              </motion.form>
            ) : (
              <motion.div className="glass p-8 text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <motion.div
                  className="h-14 w-14 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <MailIcon size={24} className="text-violet-600" />
                </motion.div>
                <div className="text-xl font-semibold">Message sent!</div>
                <p className="p mt-2">We'll get back to you within 24 hours.</p>
                <button onClick={() => setSubmitted(false)} className="btn btnGhost mt-4">Send another</button>
              </motion.div>
            )}
          </div>
        </section>
      </FXBackdrop>
    </PageTransition>
  );
}
